import React from 'react';
import { connect } from 'react-redux';

import { Box, Card, Divider, Grid } from '@mui/material/';
import { AdminRoute } from 'app/route/admin';
import { ClientRoute } from 'app/route/client';
import { SecondaryButton } from 'component/Button/Button';
import { DangerFlatButton } from 'component/DangerFlatButton/DangerFlatButton';
import { ProjectCompanyData } from 'component/ProjectCompanyData/ProjectCompanyData';
import { ProjectRate } from 'component/ProjectRate/ProjectRate';
import { ProjectStatistic } from 'component/ProjectStats/ProjectStatistic';
import { ProjectStatus } from 'component/ProjectStatus/ProjectStatus';
import { CardActonWrapper } from 'component/SingleProject/SingleProject.s';
import { ProjectModel } from 'api/dto/Project.dto';
import { AdminDispatch } from 'reduxStore/appStore';
import { openModal } from 'reduxStore/modal/slice';
import { AdminModal } from 'register/AdminModal';
import { compilePath } from 'router/compilePath';

namespace SingleProject {
  export type State = {};

  export type StateProps = {};
  export type DispatchProps = {
    onDeleteRequest: (projectId: number) => void;
    onCloseRequest: (projectId: number) => void;
    onReopenRequest: (project: ProjectModel) => void;
  };
  export type OwnProps = {
    project: ProjectModel;
  };

  export type PulseProps = {
    templateId: number;
  } & SingleProject.OwnProps;

  export type Props = OwnProps & StateProps & DispatchProps;
}

export const SingleProjectPure: React.FC<SingleProject.Props> = (props) => {
  return (
    <Box mb={2}>
      <Card data-testid="projectItem">
        <Box p={2}>
          <Grid container spacing={4} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={6} lg={4}>
              <ProjectCompanyData
                companyName={props.project.company ? props.project.company.name : ''}
                projectName={props.project.name}
                updatedAt={props.project.updatedAt}
                kind={props.project.kind}
              />
            </Grid>
            <Grid item xs={6} lg={2}>
              <ProjectStatus
                project={props.project}
                liveStart={props.project.liveStart}
                liveStop={props.project.liveStop}
              />
            </Grid>
            <Grid item xs={6} lg={2}>
              <ProjectRate rate={props.project.responseRate} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <ProjectStatistic
                partial={props.project.responsePartial || null}
                started={props.project.responseStarted || null}
                completed={props.project.responseCompleted || null}
              />
            </Grid>
          </Grid>
        </Box>
        <Divider />
        {(ProjectModel.isEditable(props.project) ||
          ProjectModel.isCloseable(props.project) ||
          ProjectModel.isDeletable(props.project)) && (
          <CardActonWrapper>
            {(ProjectModel.isEditable(props.project) ||
              ProjectModel.isFixableOnly(props.project)) && (
              <SecondaryButton
                data-testid="editButton"
                href={compilePath(AdminRoute.Project.SURVEY_PATTERN, { id: props.project.id })}
                label="Edit"
              />
            )}
            {ProjectModel.isCloseable(props.project) && (
              <SecondaryButton
                label="Close"
                onClick={() => props.onCloseRequest(props.project.id)}
              />
            )}
            {ProjectModel.isClosed(props.project) && (
              <SecondaryButton
                label="Reopen"
                onClick={() => props.onReopenRequest(props.project)}
              />
            )}
            {ProjectModel.isDeletable(props.project) && (
              <DangerFlatButton
                label="Delete"
                onClick={() => props.onDeleteRequest(props.project.id)}
              />
            )}
          </CardActonWrapper>
        )}
      </Card>
    </Box>
  );
};

export const SingleSubProjectPure = (props: SingleProject.PulseProps): React.JSX.Element => {
  return (
    <Box mb={2}>
      <Card data-testid="pulseSurveyItem">
        <Box p={2}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} sm={6}>
              <ProjectCompanyData
                projectName={props.project.name}
                updatedAt={props.project.updatedAt}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ProjectStatus
                project={props.project}
                liveStart={props.project.liveStart}
                liveStop={props.project.liveStop}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <ProjectRate rate={props.project.responseRate} />
            </Grid>
          </Grid>
        </Box>
        <Divider />
        <CardActonWrapper>
          <SecondaryButton
            data-testid="editButton"
            href={compilePath(ClientRoute.Project.Survey.Develop.QUESTION_PATTERN, {
              id: props.templateId && props.templateId.toString(),
              survey_id: props.project.id,
            })}
          >
            Edit
          </SecondaryButton>
        </CardActonWrapper>
      </Card>
    </Box>
  );
};

export const SingleProject = connect(
  null,
  (dispatch: AdminDispatch): SingleProject.DispatchProps => ({
    onDeleteRequest: (projectId: number) =>
      dispatch(openModal({ name: AdminModal.DeleteProject, params: { projectId } })),
    onCloseRequest: (projectId: number) =>
      dispatch(openModal({ name: AdminModal.CloseProject, params: { projectId } })),
    onReopenRequest: (project: ProjectModel) =>
      dispatch(openModal({ name: AdminModal.ReopenProject, params: { project } })),
  }),
)(SingleProjectPure) as React.FC<SingleProject.OwnProps>;
